import { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { loadTossPayments } from '@tosspayments/tosspayments-sdk';
import firestoreService from '../../services/firestoreService';
import { assertPaymentWindowClientKey, isTossWidgetClientKey } from '../../utils/tossKeys';

const functions = getFunctions(getApp(), 'asia-northeast3');
const getOrCreateTossCustomerKeyFn = httpsCallable(functions, 'getOrCreateTossCustomerKey');

export function getMonthlyPassAmount() {
  return Number(process.env.REACT_APP_SUBSCRIPTION_AMOUNT || 29000);
}

export function getMonthlyOrderName() {
  return process.env.REACT_APP_ORDER_NAME || 'PassRo 월 이용권';
}

/** Firestore Subscription 문서 기준 이용 기간 여부 */
export function isSubscriptionPeriodActive(sub) {
  if (!sub?.validUntil) return false;
  const v = sub.validUntil;
  const ms =
    v.seconds != null
      ? v.seconds * 1000
      : v._seconds != null
        ? v._seconds * 1000
        : NaN;
  if (!Number.isFinite(ms)) return false;
  return ms > Date.now();
}

export function useTossMonthlyPayment(user, paymentOptions = {}) {
  const { amount: overrideAmount, orderName: overrideOrderName } = paymentOptions;
  const [subscription, setSubscription] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!user?.uid) {
      setSubscription(null);
      setHistory([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const subRes = await firestoreService.getDocument('Subscription', user.uid);
    if (subRes.success && subRes.data) {
      setSubscription(subRes.data);
    } else {
      setSubscription(null);
    }
    const histRes = await firestoreService.getCollectionWithQuery('PaymentHistory', {
      field: 'userId',
      operator: '==',
      value: user.uid,
    });
    if (histRes.success && histRes.data) {
      const sorted = [...histRes.data].sort((a, b) => {
        const ta = a.createdAt?.seconds ?? a.createdAt?._seconds ?? 0;
        const tb = b.createdAt?.seconds ?? b.createdAt?._seconds ?? 0;
        return tb - ta;
      });
      setHistory(sorted);
    } else {
      setHistory([]);
    }
    setLoading(false);
  }, [user?.uid]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /**
   * 결제창 SDK 일회 결제 (API 개별 연동 키 ck 전용).
   * 결제위젯 키(gck)일 때는 PaymentManagement에서 loadPaymentWidget 사용.
   */
  const startMonthlyPayment = useCallback(async () => {
    const rawKey = process.env.REACT_APP_TOSS_CLIENT_KEY;
    if (!rawKey) {
      throw new Error('REACT_APP_TOSS_CLIENT_KEY가 설정되지 않았습니다.');
    }
    if (isTossWidgetClientKey(rawKey)) {
      throw new Error(
        '결제위젯 연동 키가 설정되어 있습니다. 화면 아래 결제위젯에서 결제해 주세요.'
      );
    }
    const clientKey = assertPaymentWindowClientKey(rawKey);
    const { data } = await getOrCreateTossCustomerKeyFn();
    const customerKey = data.customerKey;
    const tossPayments = await loadTossPayments(clientKey);
    const payment = tossPayments.payment({ customerKey });
    const origin = window.location.origin;
    const amount =
      overrideAmount != null && Number.isFinite(Number(overrideAmount))
        ? Number(overrideAmount)
        : getMonthlyPassAmount();
    const orderId = `pay_${user.uid}_${Date.now()}`;
    const orderName =
      typeof overrideOrderName === 'string' && overrideOrderName.trim()
        ? overrideOrderName.trim()
        : getMonthlyOrderName();
    const customerName =
      user?.displayName || (user?.email ? user.email.split('@')[0] : '') || '회원';

    await payment.requestPayment({
      method: 'CARD',
      amount: {
        currency: 'KRW',
        value: amount,
      },
      orderId,
      orderName,
      successUrl: `${origin}/payment/success`,
      failUrl: `${origin}/payment/fail`,
      customerEmail: user?.email || undefined,
      customerName,
      card: {
        useEscrow: false,
        flowMode: 'DEFAULT',
        useCardPoint: false,
        useAppCardOnly: false,
      },
    });
  }, [user, overrideAmount, overrideOrderName]);

  return {
    subscription,
    history,
    loading,
    startMonthlyPayment,
    refresh: loadData,
  };
}
